// @flow

/**
 * Module dependencies.
 */

import { RouteTransition } from 'react-router-transition';
import { compose } from 'recompose';
import { getLanguageFromUrl, getMetatags, translator } from 'core/utils';
import { some } from 'lodash';
import { tawkTo } from 'client/core/utils/tawk-to';
import { withRouter } from 'react-router';
import { withSticky } from 'client/components/core/sticky-navbar/with-sticky';
import CookiePrompt from 'client/components/cookie-prompt';
import Footer from 'client/components/footer';
import Layout from 'client/components/core/layout';
import Metatags from 'client/components/core/metatags';
import Navbar from 'client/components/navbar';
import React, { Component } from 'react';
import Scroll from 'react-scroll';
import entities from 'client/core/entities';
import i18n from 'client/translations/i18n';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  children: React$Element<*>,
  isSticky: boolean,
  location: Object,
  translate: Function
};

/**
 * `PageContainer` component.
 */

const PageContainer = styled.div`
  min-height: 100vh;
  position: relative;
`;

/**
 * Content wrapper style.
 */

const mapStyles = ({ opacity }: Object): Object => ({
  animationDelay: '300ms',
  bottom: 0,
  left: 0,
  minHeight: '100vh',
  opacity,
  position: opacity === 0 ? 'relative' : 'absolute',
  right: 0,
  top: 0,
  width: '100%'
});

/**
 * `App` component.
 */

class App extends Component {

  /**
   * Props.
   */

  props: Props;

  /**
   * Get location pathname.
   */

  getLocationPathname = () => {
    return location.pathname;
  }

  /**
   * Language exists.
   */

  languageExists = (language: string) => {
    const { translate } = this.props;
    const { languages } = entities(translate);

    return some(languages, ['value', language]);
  }

  /**
   * Component will receive props.
   */

  componentWillReceiveProps(nextProps: Props) {
    const { location } = nextProps;
    const scroll = Scroll.animateScroll;

    if (this.props.location.pathname !== location.pathname) {
      scroll.scrollToTop({
        delay: 0,
        duration: 0,
        offset: 0,
        smooth: false
      });
    }
  }

  /**
   * Component did mount.
   */

  componentDidMount() {
    const pathname = this.getLocationPathname();
    const locationLanguage = getLanguageFromUrl(pathname);
    const locationLanguageExists = this.languageExists(locationLanguage);
    const sameLanguage = locationLanguageExists && i18n.language === locationLanguage;

    tawkTo();

    if (sameLanguage) {
      return;
    }

    i18n.changeLanguage(locationLanguage);
  }

  /**
   * Render.
   */

  render(): React$Element<*> {
    const { children, isSticky, location, translate } = this.props;
    const { footerLinks, mainMenu } = entities(translate);
    const pathname = location.pathname;
    const year = new Date().getFullYear();

    return (
      <PageContainer>
        <RouteTransition
          atActive={{ opacity: 1 }}
          atEnter={{ opacity: 0 }}
          atLeave={{ opacity: 0 }}
          component={'div'}
          mapStyles={mapStyles}
          pathname={pathname}
        >
          <Metatags {...getMetatags(pathname, i18n.language)} />

          <Navbar
            footerLinks={footerLinks}
            isSticky={isSticky}
            links={mainMenu}
            loginLabel={translate('login')}
          />

          <Layout position={'relative'}>
            {children}

            <Footer
              author={translate('footer.author')}
              copyright={translate('footer.copyright', { year })}
              links={footerLinks}
              techPartner={translate('footer.techPartner')}
            />
          </Layout>
        </RouteTransition>

        <CookiePrompt />
      </PageContainer>
    );
  }

}

/**
 * Export `App`.
 */

export default compose(
  translator(),
  withSticky,
  withRouter
)(App);



// WEBPACK FOOTER //
// ../src/client/containers/app/index.js